// src/components/Plan/PlanCreation.jsx - PRODUCTION MODE
import React, { useState, useEffect } from 'react';
import { planService } from '../../services/planService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './PlanCreation.css';

const PlanCreation = ({ questionnaire, onSuccess, onClose }) => {
  const [planName, setPlanName] = useState('My Investment Plan');
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const { formatCurrency, getCurrencySymbol, currency } = useCurrency();

  useEffect(() => {
    fetchWalletBalance();
    fetchExchangeRate();
  }, []);

  // Refetch portfolio when currency changes
  useEffect(() => {
    const handleCurrencyChange = () => {
      fetchWalletBalance();
      fetchExchangeRate();
    };

    window.addEventListener('currencyChange', handleCurrencyChange);
    return () => window.removeEventListener('currencyChange', handleCurrencyChange);
  }, []);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/wallet/balance/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('💰 Plan - Wallet Balance Response:', data);
        
        setPortfolioSummary({
          wallet_balance: data.balance,
          available_balance: data.available_balance,
          currency: data.currency
        });
      } else {
        console.error('Failed to fetch wallet balance');
        setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
      }
    } catch (err) {
      console.error('Error fetching wallet balance:', err);
      setPortfolioSummary({ wallet_balance: 0, available_balance: 0 });
    }
  };

  const convertNGNToDisplayCurrency = (amountNGN) => {
    if (currency === 'NGN' || !exchangeRate) {
      return parseFloat(amountNGN);
    }
    
    const ngnToUsd = parseFloat(exchangeRate.ngn_to_usd);
    return parseFloat(amountNGN) * ngnToUsd;
  };

  const formatAmount = (amountNGN) => {
    const displayAmount = convertNGNToDisplayCurrency(amountNGN);
    return formatCurrency(displayAmount);
  };

  const getLockDurationText = (days) => {
    if (days < 365) {
      const weeks = Math.floor(days / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    } else {
      const years = (days / 365).toFixed(1);
      return `${years} year${parseFloat(years) !== 1 ? 's' : ''}`;
    }
  };

  const getLockEndDate = () => {
    const days = questionnaire.lock_duration_days;
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days);
    return endDate.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const getNextPeriodicBuyDate = () => {
    const nextDate = new Date();
    nextDate.setDate(nextDate.getDate() + 28); // 4 weeks = 28 days
    return nextDate.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  const getRiskProfileColor = (profile) => {
    switch (profile) {
      case 'Conservative': return '#3b82f6';
      case 'Moderate': return '#f59e0b';
      case 'Aggressive': return '#dc2626';
      default: return '#666';
    }
  };

  const calculateWeeklyGrowth = () => {
    const initialAmountNGN = parseFloat(questionnaire.initial_investment_amount);
    const weeklyGrowthNGN = initialAmountNGN * 0.0038; // 0.38%
    return convertNGNToDisplayCurrency(weeklyGrowthNGN);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const initialAmountNGN = parseFloat(questionnaire.initial_investment_amount);
    const initialAmountDisplay = convertNGNToDisplayCurrency(initialAmountNGN);
    const walletBalance = portfolioSummary ? portfolioSummary.wallet_balance : 0;
    
    console.log('📊 Plan Validation:', {
      initialAmountNGN,
      initialAmountDisplay,
      walletBalance,
      currency
    });
    
    if (portfolioSummary && initialAmountDisplay > walletBalance) {
      setError(`Insufficient funds. You have ${formatCurrency(walletBalance)} in your wallet`);
      return;
    }

    setLoading(true);

    try {
      const planData = {
        questionnaire_id: questionnaire.id,
        plan_name: planName
      };

      const response = await planService.createPlan(planData);
      onSuccess(response.plan);
    } catch (err) {
      setError(err.message || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  const isPeriodicBuy = questionnaire.buy_in_plan === 'PERIODIC_BUY';
  const weeklyGrowth = calculateWeeklyGrowth();

  return (
    <div className="plan-modal-overlay" onClick={onClose}>
      <div className="plan-creation-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="plan-modal-header">
          <div>
            <h2>Create Investment Plan</h2>
            <p className="stock-name">Build your savings with a structured plan</p>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Questionnaire Results */}
        <div className="questionnaire-results">
          <h3>Your Investment Profile</h3>
          <div className="profile-cards">
            <div className="profile-card">
              <div className="profile-label">Risk Profile</div>
              <div 
                className="profile-value risk-profile" 
                style={{ color: getRiskProfileColor(questionnaire.risk_profile) }}
              >
                {questionnaire.risk_profile}
              </div>
            </div>
            <div className="profile-card">
              <div className="profile-label">Holding Period</div>
              <div className="profile-value">
                {getLockDurationText(questionnaire.lock_duration_days)}
              </div>
            </div>
          </div>

          {/* Periodic Buy Info */}
          {isPeriodicBuy && (
            <div className="periodic-buy-banner">
              <span className="banner-icon">🔄</span>
              <div className="banner-content">
                <strong>Periodic Contributions Enabled</strong>
                <span>{formatAmount(questionnaire.periodic_investment_amount)} will be automatically added every 4 weeks</span>
              </div>
            </div>
          )}

          <div className="lock-warning">
            <span className="warning-icon">🔒</span>
            <span>⁠funds are allocated for the selected period & may not be withdrawn until <strong>{getLockEndDate()}</strong></span>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="wallet-display">
          <span className="wallet-label">Available Balance:</span>
          <span className="wallet-amount">
            {portfolioSummary ? formatCurrency(portfolioSummary.wallet_balance) : 'Loading...'}
          </span>
        </div>

        {/* Plan Form */}
        <form onSubmit={handleSubmit} className="plan-form">
          <div className="form-group">
            <label>Plan Name</label>
            <input
              type="text"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="Enter plan name"
              disabled={loading}
            />
          </div>

          <div className="plan-summary">
            <h4>Plan Summary</h4>
            <div className="summary-item">
              <span>Initial Investment:</span>
              <span>{formatAmount(questionnaire.initial_investment_amount)}</span>
            </div>
            <div className="summary-item">
              <span>Holding Period:</span>
              <span>{getLockDurationText(questionnaire.lock_duration_days)}</span>
            </div>
            <div className="summary-item">
              <span>Maturity Date:</span>
              <span>{getLockEndDate()}</span>
            </div>
            
            {/* Growth Section */}
            <div className="summary-divider"></div>
            <div className="summary-item growth">
              <span>⁠projected weekly performance:</span>
              <span>{formatCurrency(weeklyGrowth)} (0.38%)</span>
            </div>
            <div className="summary-item growth">
              <span>⁠projected annual performance:</span>
              <span>{formatCurrency(weeklyGrowth * 52)}</span>
            </div>
            
            {isPeriodicBuy && (
              <>
                <div className="summary-divider"></div>
                <div className="summary-item periodic">
                  <span>Periodic Contribution:</span>
                  <span>{formatAmount(questionnaire.periodic_investment_amount)} every 4 weeks</span>
                </div>
                <div className="summary-item periodic">
                  <span>Next Contribution Date:</span>
                  <span>{getNextPeriodicBuyDate()}</span>
                </div>
              </>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Creating Plan...' : 'Create Investment Plan'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PlanCreation;